// Faithful 2D persona art derived from the 3D Agent Office personas
// (AgentOffice3D.tsx AGENT_CONFIG): same skin tone, hair style/color, accessory
// and outfit color, drawn as a clean Mii-style bust on a dark studio background.

type Hair = "sidePart" | "spiky" | "cap" | "capBack" | "visor" | "bun";
type Accessory =
  | "readingGlasses"
  | "bigGlasses"
  | "glasses"
  | "headset"
  | "none";

interface Persona {
  skin: string;
  hairColor: string;
  hair: Hair;
  outfit: string;
  accent: string;
  accessory: Accessory;
}

// Keyed by agentId — mirrors AGENT_CONFIG order in AgentOffice3D.tsx.
export const PERSONAS: Record<string, Persona> = {
  orchestrator:               { skin: "#e8c090", hairColor: "#9a9a9a", hair: "sidePart", outfit: "#2f3a4a", accent: "#00dc96", accessory: "readingGlasses" },
  agent_1_statement_retrieval:{ skin: "#d4a070", hairColor: "#2a1a0a", hair: "cap",      outfit: "#1f6f9c", accent: "#00b8ff", accessory: "headset" },
  agent_2_bank_import:        { skin: "#f0d0a0", hairColor: "#1a1008", hair: "spiky",    outfit: "#ff9040", accent: "#ffffff", accessory: "bigGlasses" },
  agent_3_qb_posting:         { skin: "#c8a878", hairColor: "#3a2818", hair: "visor",    outfit: "#244a24", accent: "#2ca01c", accessory: "readingGlasses" },
  agent_4_invoice_collector:  { skin: "#e0b888", hairColor: "#2a1a0a", hair: "bun",      outfit: "#7a2f9c", accent: "#cc44ff", accessory: "glasses" },
  agent_5_matching:           { skin: "#d8b090", hairColor: "#0a0a0a", hair: "capBack",  outfit: "#b03048", accent: "#ff4466", accessory: "none" },
};

function darken(hex: string, f = 0.7): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * f);
  const g = Math.round(((n >> 8) & 255) * f);
  const b = Math.round((n & 255) * f);
  return `rgb(${r},${g},${b})`;
}

function Hairpiece({ p }: { p: Persona }) {
  const c = p.hairColor;
  switch (p.hair) {
    case "spiky":
      return (
        <path
          d="M30 40 L33 24 L39 36 L44 20 L50 34 L56 20 L61 36 L67 24 L70 40 Q50 30 30 40 Z"
          fill={c}
        />
      );
    case "sidePart":
      return (
        <g fill={c}>
          <path d="M28 44 Q30 22 50 22 Q70 22 72 44 Q66 32 50 32 Q40 32 36 38 Q32 40 28 44 Z" />
          <path d="M50 23 Q44 30 40 38" stroke={darken(c, 0.6)} strokeWidth="1.4" fill="none" />
        </g>
      );
    case "bun":
      return (
        <g fill={c}>
          <circle cx="50" cy="20" r="7" />
          <path d="M28 44 Q30 24 50 24 Q70 24 72 44 Q64 34 50 34 Q36 34 28 44 Z" />
        </g>
      );
    case "cap":
      return (
        <g>
          <path d="M27 40 Q30 22 50 22 Q70 22 73 40 L73 41 Q50 33 27 41 Z" fill={p.outfit} />
          <path d="M27 41 Q14 43 12 47 Q30 44 27 41 Z" fill={darken(p.outfit, 0.8)} />
          <circle cx="50" cy="24" r="2" fill={p.accent} />
        </g>
      );
    case "capBack":
      return (
        <g>
          <path d="M27 40 Q30 22 50 22 Q70 22 73 40 L73 41 Q50 33 27 41 Z" fill={p.outfit} />
          <path d="M73 41 Q86 43 88 47 Q70 44 73 41 Z" fill={darken(p.outfit, 0.8)} />
          <rect x="46" y="30" width="8" height="5" rx="1" fill={darken(p.outfit, 0.85)} />
        </g>
      );
    case "visor":
      return (
        <g>
          <path d="M28 40 Q30 28 50 28 Q70 28 72 40 Q50 34 28 40 Z" fill={c} />
          <path d="M26 40 Q30 36 50 36 Q70 36 74 40 Q76 43 72 44 Q50 39 28 44 Q24 43 26 40 Z" fill={p.outfit} />
        </g>
      );
  }
}

function Accessory({ p }: { p: Persona }) {
  const lens = (cx: number, r: number) => (
    <circle cx={cx} cy="46" r={r} fill="#ffffff" fillOpacity="0.14" stroke="#1c1c22" strokeWidth="2" />
  );
  switch (p.accessory) {
    case "bigGlasses":
      return (
        <g>
          {lens(41, 8)}
          {lens(59, 8)}
          <line x1="49" y1="46" x2="51" y2="46" stroke="#1c1c22" strokeWidth="2" />
        </g>
      );
    case "readingGlasses":
    case "glasses":
      return (
        <g>
          {lens(42, 6)}
          {lens(58, 6)}
          <line x1="48" y1="46" x2="52" y2="46" stroke="#1c1c22" strokeWidth="1.8" />
        </g>
      );
    case "headset":
      return (
        <g stroke={darken(p.outfit, 0.7)} strokeWidth="2.4" fill="none">
          <path d="M30 42 Q50 18 70 42" />
          <rect x="26" y="42" width="5" height="9" rx="2" fill={darken(p.outfit, 0.7)} stroke="none" />
          <path d="M30 50 Q28 58 40 58" stroke={darken(p.outfit, 0.7)} strokeWidth="1.6" />
          <circle cx="41" cy="58" r="1.6" fill={p.accent} stroke="none" />
        </g>
      );
    case "none":
      return null;
  }
}

/** Inline SVG persona portrait for an agent (falls back to a neutral face). */
export function PersonaAvatar({ agentId, size = 36 }: { agentId: string; size?: number }) {
  const p = PERSONAS[agentId];
  if (!p) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={agentId}>
      <defs>
        <clipPath id={`clip-${agentId}`}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>
      </defs>
      <g clipPath={`url(#clip-${agentId})`}>
        {/* studio background */}
        <rect width="100" height="100" fill="#2a3440" />
        <rect width="100" height="50" fill="#313c49" />
        {/* shoulders / outfit */}
        <path d="M16 100 Q18 74 50 74 Q82 74 84 100 Z" fill={p.outfit} />
        <path d="M50 74 L50 100" stroke={darken(p.outfit, 0.82)} strokeWidth="1.5" />
        {/* neck */}
        <rect x="44" y="62" width="12" height="12" rx="4" fill={darken(p.skin, 0.92)} />
        {/* ears */}
        <circle cx="30" cy="48" r="5" fill={p.skin} />
        <circle cx="70" cy="48" r="5" fill={p.skin} />
        {/* head */}
        <ellipse cx="50" cy="46" rx="22" ry="24" fill={p.skin} />
        {/* hair behind/over */}
        <Hairpiece p={p} />
        {/* eyes */}
        <ellipse cx="42" cy="47" rx="2.4" ry="3" fill="#1c1c22" />
        <ellipse cx="58" cy="47" rx="2.4" ry="3" fill="#1c1c22" />
        {/* brows */}
        <path d="M38 41 Q42 39 46 41" stroke={darken(p.hairColor, 0.9)} strokeWidth="1.4" fill="none" />
        <path d="M54 41 Q58 39 62 41" stroke={darken(p.hairColor, 0.9)} strokeWidth="1.4" fill="none" />
        {/* mouth */}
        <path d="M45 57 Q50 60 55 57" stroke={darken(p.skin, 0.6)} strokeWidth="1.6" fill="none" />
        {/* accessory on top */}
        <Accessory p={p} />
      </g>
    </svg>
  );
}
